import { useId, useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { cn } from './cn';

/**
 * The filters above a list, and the chips that say what is on.
 *
 * Two things it fixes that hand-rolled filter rows keep getting wrong.
 *
 * First, **what is currently filtered must be visible without opening
 * anything.** A dropdown that reads "Status" whether or not a status is
 * selected leaves somebody staring at 12 rows of a 4,000-row table wondering
 * where everything went — and then reporting it as missing data. Every active
 * filter gets a chip with its value spelled out.
 *
 * Second, **clearing has to be one action.** Removing four filters one at a
 * time, each triggering its own fetch, is how people give up and reload the
 * page instead.
 *
 * On a phone the selects fold behind one "Filters" button that says how many
 * are on. A search box and four selects stacked took the first screen, and
 * the list they filter started below it. The search stays out; the chips
 * under the bar still say what is filtered while the fold is shut. From `md`
 * up nothing changes: the fold is `display: contents` there.
 */

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSpec {
  key: string;
  label: string;
  options: FilterOption[];
  /** Shown when nothing is chosen. Defaults to "All". */
  allLabel?: string;
}

export interface FilterBarProps {
  filters: FilterSpec[];
  /** Current values. A key that is absent or empty means "not filtered". */
  value: Record<string, string | undefined>;
  onChange: (next: Record<string, string | undefined>) => void;
  /** Search — what stays in view on a phone. */
  children?: ReactNode;
  /**
   * Filter controls that are not one of `filters` — a date range, a window
   * of days. In line on a desktop; inside the fold on a phone.
   */
  more?: ReactNode;
  /** How many of the `more` controls are away from their default, for the button's count. */
  moreActive?: number;
  /** The primary action for the screen. */
  actions?: ReactNode;
  className?: string;
}

export function FilterBar({
  filters, value, onChange, children, more, moreActive = 0, actions, className,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const foldId = useId();
  const active = useMemo(
    () => filters
      .map((f) => ({ spec: f, chosen: value[f.key] }))
      .filter((x): x is { spec: FilterSpec; chosen: string } => Boolean(x.chosen)),
    [filters, value],
  );

  const set = (key: string, next: string) =>
    // An empty string is "not filtered", not a filter for the empty value —
    // otherwise choosing "All" sends `?status=` and the API filters on it.
    onChange({ ...value, [key]: next || undefined });

  const clearAll = () =>
    onChange(Object.fromEntries(filters.map((f) => [f.key, undefined])));

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {children}

        {(filters.length > 0 || more) && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={foldId}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-input border px-3 text-small font-medium md:hidden',
              active.length + moreActive > 0
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-neutral-300 bg-white text-neutral-700',
            )}
          >
            <SlidersHorizontal aria-hidden className="h-4 w-4" />
            Filters{active.length + moreActive > 0 ? ` (${active.length + moreActive})` : ''}
            <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
          </button>
        )}

        {/* `contents` from md: the selects are the bar's own children there,
            exactly as before. Below it they are one block, shown on demand. */}
        <div id={foldId} className={cn(open ? 'flex' : 'hidden', 'w-full flex-col gap-2 md:contents')}>
          {more}
          {filters.map((f) => (
            <label key={f.key} className="inline-flex min-w-0 max-w-full items-center max-md:w-full">
              <span className="sr-only">{f.label}</span>
              <select
                value={value[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
                className={cn(
                  // The height of the SearchInput that usually sits beside them.
                  'h-10 min-w-0 max-w-full rounded-input border px-2.5 text-small font-medium max-md:w-full',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                  value[f.key]
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-neutral-300 bg-white text-neutral-700',
                )}
              >
                <option value="">{f.allLabel ?? `${f.label}: all`}</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          ))}
        </div>

        {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {active.map(({ spec, chosen }) => (
            <span
              key={spec.key}
              className="inline-flex items-center gap-1 rounded-badge bg-neutral-100 py-0.5 pl-2 pr-1 text-caption text-neutral-700"
            >
              <span className="text-neutral-500">{spec.label}:</span>
              {spec.options.find((o) => o.value === chosen)?.label ?? chosen}
              <button
                type="button"
                onClick={() => set(spec.key, '')}
                aria-label={`Remove ${spec.label} filter`}
                className="-my-1 inline-flex h-6 w-6 items-center justify-center rounded text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 max-md:h-10 max-md:w-9"
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            </span>
          ))}
          {active.length > 1 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-caption font-medium text-primary-600 hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
